import { useEffect, useCallback, useRef } from 'react';

/**
 * Client-side Socket Event Management Hook
 * 
 * Provides centralized socket event handling with:
 * - Standardized event listener management
 * - Automatic cleanup on unmount
 * - Event validation and error handling
 * - Debug logging capabilities
 * 
 * Phase 3: Socket Event System Cleanup
 */

// Client-side socket event constants (matching server-side)
export const SOCKET_EVENTS = {
  // Connection Events
  CONNECTION: {
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    ERROR: 'error',
  },

  // Room Management Events
  ROOM: {
    ROOM_JOINED: 'server:room:joined',
    ROOM_LEFT: 'server:room:left',
    ROOM_ERROR: 'server:room:error',
  },

  // Message Events
  MESSAGE: {
    NEW: 'server:message:new',
  },

  // Conversation Events
  CONVERSATION: {
    MODE_CHANGED: 'server:conversation:mode_changed',
  },

  // AI Suggestion Events
  AI_SUGGESTION: {
    TEXT: 'server:ai:suggestion_text',
    MODE_CHANGE: 'server:ai:suggestion_mode_change',
  },

  // Goal Events
  GOAL: {
    PROPOSAL_FOR_THERAPIST: 'server:goal:proposal_for_therapist',
    PROPOSAL_FOR_CLIENT: 'server:goal:proposal_for_client',
    ACTION_UPDATE: 'server:goal:action_update',
  },

  // Assessment Events
  ASSESSMENT: {
    PROPOSAL_FOR_THERAPIST: 'server:assessment:proposal_for_therapist',
    REQUEST_FOR_CLIENT: 'server:assessment:request_for_client',
    COMPLETED: 'server:assessment:completed',
  },

  // Legacy event names for backward compatibility
  LEGACY: {
    AI_SUGGESTION: 'ai_suggestion',
    NEW_MESSAGE: 'new_message',
    CONTROL_MODE_CHANGED: 'control_mode_changed',
    AI_GOAL_PROPOSAL_FOR_THERAPIST: 'ai_goal_proposal_for_therapist',
    AI_ASSESSMENT_PROPOSAL_FOR_THERAPIST: 'ai_assessment_proposal_for_therapist',
    AI_MODE_CHANGE_SUGGESTION: 'ai_mode_change_suggestion',
    CLIENT_GOAL_ACTION_UPDATE: 'client_goal_action_update',
  }
};

const useSocketEventManager = (socket, options = {}) => {
  const { 
    debugMode = process.env.NODE_ENV === 'development',
    enableLegacySupport = true,
    validateEvents = true 
  } = options;

  const eventListeners = useRef(new Map());
  const eventStats = useRef({
    eventsReceived: 0,
    eventsHandled: 0,
    errors: 0
  });

  /**
   * Validate event envelope structure
   * @param {Object} eventData - The received event data
   * @returns {boolean} Whether the event is valid
   */
  const validateEventEnvelope = useCallback((eventData) => {
    if (!validateEvents) return true;
    
    // Check if it's a standardized envelope
    if (eventData && typeof eventData === 'object' && 'eventType' in eventData) {
      return !!(eventData.eventType && eventData.timestamp && eventData.data !== undefined);
    }
    
    // Allow legacy events to pass through
    return true;
  }, [validateEvents]);

  /**
   * Enhanced event listener with validation and error handling
   * @param {string} eventName - Event name to listen for
   * @param {Function} handler - Event handler function
   * @param {Object} handlerOptions - Handler options
   */
  const addEventListener = useCallback((eventName, handler, handlerOptions = {}) => {
    if (!socket) {
      console.warn('Socket not available, cannot add event listener for:', eventName);
      return;
    }

    const enhancedHandler = (eventData) => {
      eventStats.current.eventsReceived++;

      try {
        // Validate event structure
        if (!validateEventEnvelope(eventData)) {
          console.warn(`Invalid event envelope for ${eventName}:`, eventData);
          eventStats.current.errors++;
          return;
        }

        // Log in debug mode
        if (debugMode) {
          console.log(`📨 Received ${eventName}:`, 
                     handlerOptions.logData !== false ? eventData : '[data hidden]');
        }

        // Extract data from envelope or use raw data for legacy events
        const processedData = eventData && typeof eventData === 'object' && 'data' in eventData 
          ? eventData.data 
          : eventData;

        // Call the original handler
        handler(processedData, eventData);
        eventStats.current.eventsHandled++;

      } catch (error) {
        console.error(`Error handling socket event ${eventName}:`, error);
        eventStats.current.errors++;
        
        if (handlerOptions.onError) {
          handlerOptions.onError(error, eventData);
        }
      }
    };

    // Store reference for cleanup
    eventListeners.current.set(eventName, enhancedHandler);

    // Add the listener
    socket.on(eventName, enhancedHandler);

    if (debugMode) {
      console.log(`✓ Added socket listener for: ${eventName}`);
    }
  }, [socket, debugMode, validateEventEnvelope]);

  /**
   * Remove a specific event listener
   * @param {string} eventName - Event name to remove
   */
  const removeEventListener = useCallback((eventName) => {
    if (!socket) return;

    const handler = eventListeners.current.get(eventName);
    if (handler) {
      socket.off(eventName, handler);
      eventListeners.current.delete(eventName);
      
      if (debugMode) {
        console.log(`✓ Removed socket listener for: ${eventName}`);
      }
    }
  }, [socket, debugMode]);

  /**
   * Add multiple event listeners at once
   * @param {Object} eventMap - Map of event names to handlers
   * @param {Object} commonOptions - Common options for all handlers
   */
  const addEventListeners = useCallback((eventMap, commonOptions = {}) => {
    Object.entries(eventMap).forEach(([eventName, handler]) => {
      addEventListener(eventName, handler, commonOptions);
    });
  }, [addEventListener]);

  /**
   * Emit client-side events with standardized format
   * @param {string} eventName - Event name to emit
   * @param {Object} data - Event data
   */
  const emitEvent = useCallback((eventName, data) => {
    if (!socket) {
      console.warn('Socket not available, cannot emit event:', eventName);
      return;
    }

    try {
      socket.emit(eventName, data);
      
      if (debugMode) {
        console.log(`📤 Emitted ${eventName}:`, data);
      }
    } catch (error) {
      console.error(`Error emitting socket event ${eventName}:`, error);
    }
  }, [socket, debugMode]);

  /**
   * Join a room with standardized format
   * @param {string} roomType - Type of room ('conversation' or 'user')
   * @param {string} id - Room identifier
   */
  const joinRoom = useCallback((roomType, id) => {
    const eventName = roomType === 'conversation' 
      ? 'client:room:join_conversation'
      : 'therapist:room:join_conversation';
    
    emitEvent(eventName, id);
  }, [emitEvent]);

  /**
   * Leave a room with standardized format
   * @param {string} roomType - Type of room ('conversation' or 'user')
   * @param {string} id - Room identifier
   */
  const leaveRoom = useCallback((roomType, id) => {
    const eventName = roomType === 'conversation'
      ? 'client:room:leave_conversation'
      : 'therapist:room:leave_conversation';
    
    emitEvent(eventName, id);
  }, [emitEvent]);

  /**
   * Get event statistics for debugging
   * @returns {Object} Event statistics
   */
  const getEventStats = useCallback(() => {
    return { ...eventStats.current };
  }, []);

  /**
   * Clear all event listeners
   */
  const clearAllListeners = useCallback(() => {
    if (!socket) return;

    eventListeners.current.forEach((handler, eventName) => {
      socket.off(eventName, handler);
    });
    
    eventListeners.current.clear();
    
    if (debugMode) {
      console.log('✓ Cleared all socket event listeners');
    }
  }, [socket, debugMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllListeners();
    };
  }, [clearAllListeners]);

  // Connection status tracking
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      if (debugMode) {
        console.log('🔌 Socket connected');
      }
    };

    const handleDisconnect = (reason) => {
      if (debugMode) {
        console.log('🔌 Socket disconnected:', reason);
      }
    };

    const handleError = (error) => {
      console.error('🔌 Socket error:', error);
      eventStats.current.errors++;
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('error', handleError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('error', handleError);
    };
  }, [socket, debugMode]);

  return {
    // Event management
    addEventListener,
    removeEventListener,
    addEventListeners,
    clearAllListeners,
    
    // Room management
    joinRoom,
    leaveRoom,
    
    // Utilities
    emitEvent,
    getEventStats,
    
    // Constants
    SOCKET_EVENTS,
    
    // Connection info
    isConnected: socket?.connected || false
  };
};

export default useSocketEventManager;